import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { WordMark } from "./Logo";
import LangToggle from "./LangToggle";
import { useI18n } from "@/i18n/I18nContext";

const LEGAL_CONTENT = {
  en: {
    terms: `Effective date: December 2025. These Terms govern your purchase and use of the ebook "From Milk to Income" (the "Book") sold through the website milk2cash on the nutriora.food domain (the "Service"), operated by Nutriora Foods (Tanzania) ("we", "us"). By completing a purchase you confirm you have read the Important Notice shown above and accept these Terms in full.

LICENSE. Your purchase grants you a single, non-transferable, non-exclusive personal-use licence to the digital PDF, which is watermarked with your name, email and phone number on every page. You may print one copy for your personal use. You may not resell, re-distribute, sub-licence, upload publicly, remove the watermark, or share the PDF or any excerpt of it. Watermarked copies linked to piracy will have their licence revoked and may be reported.

INTELLECTUAL PROPERTY. All content, text, illustrations, working templates and brand marks (including "Milk2Income" and "Nutriora Foods") remain the exclusive property of Nutriora Foods and its authors and are protected by applicable copyright and trademark laws.

PAYMENTS. All payments are processed by Paddle.com Market Limited ("Paddle"), which acts as the merchant of record for your transaction, handles billing, tax calculation where applicable, and payment-method data. Nutriora Foods never sees or stores your card number. By completing checkout you also accept Paddle's own terms and privacy policy.

REFUNDS. We offer a 7-day happiness guarantee — see the Refund Policy for the full process. Beyond that window, refunds are considered only in cases of material defect in the delivered PDF.

NO GUARANTEE. As stated in the Important Notice, no income, profit or business outcome is guaranteed. You accept full responsibility for verifying regulations, permits and safety standards with the Tanzania Dairy Board (TDB), the Tanzania Bureau of Standards (TBS) and your local authority before starting any commercial dairy activity.

LIABILITY. To the fullest extent permitted by law, Nutriora Foods disclaims liability for any indirect, incidental, special or consequential damages arising from your use of the Book or the Service. Nothing in these Terms limits rights that cannot be limited by law.

GOVERNING LAW. These Terms are governed by the laws of the United Republic of Tanzania. Any dispute shall first be resolved amicably by writing to info@nutriora.food; failing that, in the competent courts of Dar es Salaam.

UPDATES. We may update these Terms and will note the new effective date above. Continued use of the Service after an update constitutes acceptance of the revised Terms.

CONTACT. info@nutriora.food · Nutriora Foods, Tanzania.`,

    privacy: `Effective date: December 2025. This Privacy Policy explains how Nutriora Foods ("we") handles personal data submitted through the milk2cash section of nutriora.food (the "Service").

DATA WE COLLECT. We collect only the personal information you provide at checkout: your full name, email address and phone number. We do not collect card or bank-account data — all payment data is handled directly by Paddle.com Market Limited.

HOW WE USE IT. Your data is used strictly to (1) deliver your personalised watermarked PDF, (2) embed your name, email and phone number as an anti-piracy watermark on every page of the delivered PDF, (3) send you order-related transactional emails, and (4) alert the Nutriora Foods team of a new sale so we can respond to any issues personally.

LEGAL BASIS. We process this data under contract (to deliver your purchase) and legitimate interest (anti-piracy watermarking, fraud prevention).

SHARING. We never sell your data. We share your name/email/phone only with the service providers strictly necessary to deliver the product: Paddle (payment processing), Resend (transactional email), and MongoDB Atlas (encrypted storage).

RETENTION. Order records are retained for 7 years to meet accounting and tax obligations. You may request earlier deletion of your personal data (name, phone) by writing to info@nutriora.food; the record of the sale itself must be retained by law.

YOUR RIGHTS. You can request access to, correction of, or deletion of your personal data at any time by writing to info@nutriora.food. We will respond within 30 days.

COOKIES. The site uses only minimal technical cookies required for the Paddle checkout session and anonymous privacy-respecting analytics. No advertising cookies.

SECURITY. All data is transmitted over HTTPS and stored encrypted at rest. The watermarked PDF is served from a private URL bound to your order.

CONTACT. info@nutriora.food · Nutriora Foods, Tanzania.`,

    refund: `Effective date: December 2025. We stand behind the ebook with a straightforward 7-day happiness guarantee.

ELIGIBILITY. If, within 7 calendar days of your purchase, you feel the ebook has not delivered on the promises made on the nutriora.food/milk2cash sales page, you are entitled to a full refund with no further questions asked.

HOW TO CLAIM. Email info@nutriora.food from the same email address used at checkout, quoting your Paddle transaction ID or Milk2Income order ID (M2I-…). Please add one line about what fell short — not required for a refund, but it helps us improve the next edition.

PROCESSING. Refunds are issued through Paddle to the original payment method and usually arrive within 5-10 business days. You will receive a refund confirmation email from Paddle.

LICENCE REVOCATION. On refund, your personal-use licence to the PDF is revoked and the download link is deactivated. The watermarked copy you have already downloaded remains with you but may not be shared, re-sold or distributed under any circumstances.

OUTSIDE 7 DAYS. Beyond the 7-day window we do not offer refunds for change of mind, but if the delivered PDF has a material defect (missing pages, corrupted file, wrong watermark) we will of course fix it or refund.

CONTACT. info@nutriora.food · Nutriora Foods, Tanzania.`,
  },

  sw: {
    terms: `Tarehe ya kuanza: Desemba 2025. Vigezo hivi vinasimamia ununuzi na matumizi yako ya kitabu "From Milk to Income" ("Kitabu") kinachouzwa kupitia tovuti ya milk2cash kwenye kikoa cha nutriora.food ("Huduma"), inayoendeshwa na Nutriora Foods (Tanzania) ("sisi"). Kwa kukamilisha ununuzi unathibitisha umesoma Taarifa Muhimu iliyopo hapo juu na unakubali Vigezo hivi kikamilifu.

LESENI. Ununuzi wako unakupatia leseni ya mtu binafsi, isiyohamishika, isiyoshirikiwa kwa PDF ya dijitali, iliyowekwa alama ya maji ya jina, barua pepe na simu yako kwenye kila ukurasa. Unaweza kuchapisha nakala moja kwa matumizi yako binafsi. Huwezi kuuza tena, kusambaza, kutoa leseni ya pili, kupakia hadharani, kuondoa alama ya maji, wala kushirikisha PDF au sehemu yake yoyote.

HAKI ZA UBUNIFU. Yaliyomo yote, maandishi, michoro, fomu za kazi na alama za chapa (ikijumuisha "Milk2Income" na "Nutriora Foods") vinabaki mali ya Nutriora Foods na waandishi wake, zikilindwa na sheria za hakimiliki na alama ya biashara zinazotumika.

MALIPO. Malipo yote yanashughulikiwa na Paddle.com Market Limited ("Paddle"), ambayo inafanya kazi kama muuzaji rasmi wa shughuli yako, inashughulikia malipo, kodi inapohitajika, na data ya njia ya malipo. Nutriora Foods haioni wala kuhifadhi nambari ya kadi yako. Kwa kukamilisha malipo unakubali pia vigezo na sera ya faragha ya Paddle.

MAREJESHO. Tunatoa uhakikisho wa furaha wa siku 7 — angalia Sera ya Marejesho kwa mchakato kamili.

HAKUNA DHAMANA. Kama ilivyoelezwa katika Taarifa Muhimu, hakuna kipato, faida au matokeo ya biashara yanayohakikishwa. Unakubali jukumu kamili la kuhakiki sheria, vibali na viwango vya usalama kutoka Bodi ya Maziwa Tanzania (TDB), Shirika la Viwango Tanzania (TBS) na mamlaka ya eneo lako kabla ya kuanza biashara yoyote ya maziwa.

UWAJIBIKAJI. Kwa kiwango kinachoruhusiwa na sheria, Nutriora Foods inakataa uwajibikaji wa uharibifu wa aina yoyote unaotokana na matumizi ya Kitabu au Huduma. Hakuna kitu katika Vigezo hivi kinachopunguza haki zisizoweza kupunguzwa kisheria.

SHERIA. Vigezo hivi vinatawaliwa na sheria za Jamhuri ya Muungano wa Tanzania. Mgogoro wowote utatatuliwa kwa amani kwanza kwa kuandika info@nutriora.food; kama haiwezekani, kwenye mahakama za Dar es Salaam.

MASASISHO. Tunaweza kusasisha Vigezo hivi na tutaonyesha tarehe mpya ya kuanza hapo juu. Kuendelea kutumia Huduma baada ya sasisho ni kukubali Vigezo vilivyosasishwa.

MAWASILIANO. info@nutriora.food · Nutriora Foods, Tanzania.`,

    privacy: `Tarehe ya kuanza: Desemba 2025. Sera hii ya Faragha inaeleza jinsi Nutriora Foods ("sisi") inavyoshughulikia data binafsi iliyowasilishwa kupitia sehemu ya milk2cash ya nutriora.food (Huduma).

DATA TUNAYOKUSANYA. Tunakusanya tu taarifa binafsi unazotoa wakati wa malipo: jina lako kamili, anwani ya barua pepe na nambari ya simu. Hatukusanyi data ya kadi au akaunti ya benki — data yote ya malipo inashughulikiwa moja kwa moja na Paddle.com Market Limited.

JINSI TUNAVYOITUMIA. Data yako inatumika tu kwa (1) kutuma PDF yako binafsi yenye alama ya maji, (2) kuweka jina, barua pepe na simu yako kama alama ya kuzuia wizi kwenye kila ukurasa, (3) kukutumia barua pepe za shughuli zinazohusiana na oda, na (4) kujulisha timu ya Nutriora Foods juu ya mauzo mapya ili tuweze kujibu masuala yoyote mwenyewe.

MSINGI WA KISHERIA. Tunashughulikia data hii chini ya mkataba (kutuma ununuzi wako) na maslahi halali (alama ya kuzuia wizi, kuzuia udanganyifu).

KUSHIRIKI. Hatuuzi data yako. Tunashirikisha jina/barua pepe/simu yako tu na watoa huduma wanaohitajika kwa ajili ya kutuma bidhaa: Paddle (malipo), Resend (barua pepe), na MongoDB Atlas (hifadhi iliyosimbwa).

UHIFADHI. Kumbukumbu za oda zinahifadhiwa kwa miaka 7 kutimiza majukumu ya uhasibu na kodi. Unaweza kuomba kufutwa mapema kwa data yako binafsi (jina, simu) kwa kuandika info@nutriora.food; kumbukumbu ya mauzo yenyewe lazima ihifadhiwe kisheria.

HAKI ZAKO. Unaweza kuomba kufikia, kurekebisha au kufuta data yako binafsi wakati wowote kwa kuandika info@nutriora.food. Tutajibu ndani ya siku 30.

VIDAKUZI. Tovuti inatumia tu vidakuzi vya kiufundi vya chini zaidi vinavyohitajika kwa kipindi cha malipo cha Paddle na uchambuzi unaoheshimu faragha. Hakuna vidakuzi vya matangazo.

USALAMA. Data yote inatumwa kupitia HTTPS na inahifadhiwa ikiwa imesimbwa. PDF yenye alama ya maji inatolewa kutoka URL ya faragha iliyofungamanishwa na oda yako.

MAWASILIANO. info@nutriora.food · Nutriora Foods, Tanzania.`,

    refund: `Tarehe ya kuanza: Desemba 2025. Tunauthibitishia kitabu kwa uhakikisho wa furaha wa siku 7 wa moja kwa moja.

USTAHIKI. Ikiwa, ndani ya siku 7 za kalenda tangu ununuzi wako, unahisi kitabu hakikutimiza ahadi zilizotolewa kwenye ukurasa wa mauzo wa nutriora.food/milk2cash, una haki ya kurudishiwa fedha kamili bila maswali zaidi.

JINSI YA KUDAI. Tuma barua pepe info@nutriora.food kutoka kwa anwani sawa iliyotumika wakati wa malipo, ukitaja Namba ya shughuli ya Paddle au Namba ya oda ya Milk2Income (M2I-…). Tafadhali ongeza mstari mmoja kuhusu nini hakikuridhika — si lazima kwa marejesho, lakini inatusaidia kuboresha toleo linalofuata.

USHUGHULIKAJI. Marejesho yanatolewa kupitia Paddle kwa njia ya malipo ya awali na kawaida yanawasili ndani ya siku 5-10 za kazi. Utapokea barua pepe ya uthibitisho wa marejesho kutoka Paddle.

KUFUTWA KWA LESENI. Baada ya kurudishiwa, leseni yako ya matumizi binafsi ya PDF inafutwa na kiungo cha kupakua kinazimwa. Nakala yenye alama ya maji uliyopakua tayari inabaki nawe lakini haiwezi kushirikishwa, kuuzwa tena au kusambazwa kwa hali yoyote.

NJE YA SIKU 7. Nje ya kipindi cha siku 7 hatutoi marejesho ya mabadiliko ya mawazo, lakini ikiwa PDF iliyotumwa ina kasoro halisi (kurasa zilizokosa, faili iliyoharibika, alama ya maji isiyo sahihi) tutairekebisha au kurudisha fedha.

MAWASILIANO. info@nutriora.food · Nutriora Foods, Tanzania.`,
  },
};

const LINKS = [
  { label: "Buy the book", href: "#pricing" },
  { label: "Inside the book", href: "#inside" },
  { label: "Templates", href: "#templates" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
];

export default function Footer({ onBuy }) {
  const { t, locale } = useI18n();
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showRefund, setShowRefund] = useState(false);

  const scrollTo = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const legal = LEGAL_CONTENT[locale] || LEGAL_CONTENT.en;

  return (
    <footer
      className="relative pt-24 pb-8 text-[var(--cream)]"
      style={{ background: "var(--ink)" }}
      data-testid="footer"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 pb-16 border-b border-[rgba(253,251,247,0.14)]">
          <div>
            <div className="chip-num mb-4" style={{ color: "var(--marigold-2)" }}>
              {t.footer.chip}
            </div>
            <h2 className="font-display font-medium text-[2.6rem] sm:text-[3.4rem] lg:text-[4.4rem] leading-[0.98] tracking-[-0.03em]">
              {t.footer.h2_a}
              <br />
              <span className="font-editorial-italic text-[var(--marigold-2)]">
                {t.footer.h2_italic}
              </span>
            </h2>
          </div>
          <div className="flex flex-col justify-end gap-4 text-[var(--cream)]/70 text-[15px] leading-relaxed">
            <p>{t.footer.p}</p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={onBuy}
                className="btn-pill btn-primary text-[15px]"
                data-testid="footer-buy-button"
              >
                <span>{t.footer.cta}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <LangToggle variant="dark" />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-10 py-14">
          <div>
            <WordMark />
            <p className="mt-4 text-[13.5px] text-[var(--cream)]/60 max-w-[36ch] leading-relaxed">
              {t.footer.about}
            </p>
          </div>
          <div>
            <div className="font-mono-caption text-[10px] tracking-[0.22em] uppercase text-[var(--cream)]/40 mb-4">
              {t.footer.navigate}
            </div>
            <ul className="space-y-2">
              {t.footer.links.map((l) => (
                <li key={l.href}>
                  {l.route ? (
                    <Link
                      to={l.href}
                      className="text-[var(--cream)]/75 hover:text-[var(--marigold-2)] transition-colors text-[15px]"
                      data-testid="footer-affiliate-link"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <a
                      href={l.href}
                      onClick={(e) => scrollTo(e, l.href)}
                      className="text-[var(--cream)]/75 hover:text-[var(--marigold-2)] transition-colors text-[15px]"
                    >
                      {l.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-mono-caption text-[10px] tracking-[0.22em] uppercase text-[var(--cream)]/40 mb-4">
              {t.footer.legal}
            </div>
            <ul className="space-y-2 text-[15px]">
              <li>
                <button
                  onClick={() => setShowTerms(true)}
                  className="text-[var(--cream)]/75 hover:text-[var(--marigold-2)] transition-colors"
                  data-testid="footer-terms-link"
                >
                  {t.footer.terms}
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowPrivacy(true)}
                  className="text-[var(--cream)]/75 hover:text-[var(--marigold-2)] transition-colors"
                  data-testid="footer-privacy-link"
                >
                  {t.footer.privacy}
                </button>
              </li>
              <li>
                <button
                  onClick={() => setShowRefund(true)}
                  className="text-[var(--cream)]/75 hover:text-[var(--marigold-2)] transition-colors"
                  data-testid="footer-refund-link"
                >
                  {t.footer.refund}
                </button>
              </li>
              <li>
                <a
                  href={`mailto:${t.footer.contact}`}
                  className="text-[var(--cream)]/75 hover:text-[var(--marigold-2)] transition-colors"
                >
                  {t.footer.contact}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-6 border-t border-[rgba(253,251,247,0.1)] flex flex-col md:flex-row items-center justify-between gap-4 text-[12px] text-[var(--cream)]/50">
          <div>© {new Date().getFullYear()} {t.footer.copy_left}</div>
          <div className="flex items-center gap-3">
            <span>{t.footer.pays_by}</span>
            <span className="font-display italic text-[var(--cream)]/80">Paddle</span>
            <span className="w-px h-3 bg-[rgba(253,251,247,0.2)]" />
            <span>{t.footer.made_in}</span>
          </div>
        </div>
      </div>

      <LegalModal open={showTerms} onClose={() => setShowTerms(false)} title={t.footer.terms} body={legal.terms} closeLabel={t.footer.close} testid="terms-modal" />
      <LegalModal open={showPrivacy} onClose={() => setShowPrivacy(false)} title={t.footer.privacy} body={legal.privacy} closeLabel={t.footer.close} testid="privacy-modal" />
      <LegalModal open={showRefund} onClose={() => setShowRefund(false)} title={t.footer.refund} body={legal.refund} closeLabel={t.footer.close} testid="refund-modal" />
    </footer>
  );
}

const LegalModal = ({ open, onClose, title, body, closeLabel, testid }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        data-testid={testid}
      >
        <div className="absolute inset-0 bg-[rgba(15,31,23,0.75)] backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
          className="relative bg-[var(--cream)] text-[var(--ink)] rounded-3xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        >
          <div className="p-6 md:p-8 flex items-center justify-between border-b border-[rgba(15,31,23,0.08)]">
            <h3 className="font-display text-2xl md:text-3xl tracking-tight">{title}</h3>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full border border-[rgba(15,31,23,0.15)] hover:bg-[var(--cream-2)] flex items-center justify-center"
              aria-label={closeLabel}
              data-testid={`${testid}-close`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
          <div className="overflow-y-auto p-6 md:p-8 text-[14.5px] leading-[1.75] text-[var(--ink-2)]/85 whitespace-pre-line">
            {body}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
